import type { AdAccountCapacity, AdAccountSource, AdAccountsResponse, AdPlatform } from '../types';

/**
 * Where an ad account in the Deliver-to panel comes from.
 *
 * - `metaCustomer`: a Meta account the brand connected.
 * - `metaProxima`: a Meta account Proxima owns, listed by proxima-audiences.
 * - `tiktokCustomer`: a TikTok account the brand connected.
 */
export type AdAccountGroup = 'metaCustomer' | 'metaProxima' | 'tiktokCustomer';

export type AdAccountOption = {
  group: AdAccountGroup;
  adAccountId: string;
  name: string;
  /** Meta's custom audience terms; null where the group carries no such flag. */
  acceptedTos: boolean | null;
  /** Free custom audience slots on a Proxima account; null on brand accounts. */
  capacity: AdAccountCapacity | null;
};

export const INELIGIBLE_REASONS = {
  proximaOnTiktok: 'Not available on TikTok',
  notTiktok: 'Not a TikTok account',
  notMeta: 'Not a Meta account',
  mixedGroups: "Customer and Proxima accounts can't be mixed in one delivery",
  tosNotAccepted: "Ad account has not accepted Meta's custom audience terms",
  noFreeSlots: "Ad account is at Meta's custom audience limit",
  unknownAccount: 'Ad account is not in the loaded ad account lists'
} as const;

export type IneligibleReason = (typeof INELIGIBLE_REASONS)[keyof typeof INELIGIBLE_REASONS];

export type AdAccountEligibility =
  | { eligible: true }
  | { eligible: false; reason: IneligibleReason };

/** The platform each ad account group delivers to. */
export const GROUP_PLATFORM: Record<AdAccountGroup, AdPlatform> = {
  metaCustomer: 'meta',
  metaProxima: 'meta',
  tiktokCustomer: 'tiktok'
};

/** The source each ad account group is loaded from; see `AdAccountSource`. */
export const GROUP_SOURCE: Record<AdAccountGroup, AdAccountSource> = {
  metaCustomer: 'brand',
  metaProxima: 'proxima',
  tiktokCustomer: 'brand'
};

export const NO_AD_ACCOUNT_PICKED = 'Pick at least one ad account';

/**
 * Every ad account in panel order: brand Meta accounts, Proxima accounts, brand TikTok accounts.
 *
 * - A brand Meta account whose id is on the Proxima list is listed once, as Proxima-owned, since proxima-audiences flags a destination by that list.
 * - Proxima accounts on a platform other than Meta are left out, since only Meta takes them.
 */
export const toAdAccountOptions = (adAccounts: AdAccountsResponse | null): AdAccountOption[] => {
  const proximaMeta = (adAccounts?.proximaAdAccountsList ?? []).filter(
    account => account.platform === 'meta'
  );
  const proximaIds = new Set(proximaMeta.map(account => account.adAccountId));
  return [
    ...(adAccounts?.metaAdAccountsList ?? [])
      .filter(account => !proximaIds.has(account.adAccountId))
      .map(account => ({
        group: 'metaCustomer' as const,
        adAccountId: account.adAccountId,
        name: account.name,
        acceptedTos: Boolean(account.properties?.acceptedTos),
        capacity: null
      })),
    ...proximaMeta.map(account => ({
      group: 'metaProxima' as const,
      adAccountId: account.adAccountId,
      name: account.name,
      acceptedTos: null,
      capacity: account.capacity
    })),
    ...(adAccounts?.tiktokAdAccountsList ?? []).map(account => ({
      group: 'tiktokCustomer' as const,
      adAccountId: account.adAccountId,
      name: account.name,
      acceptedTos: null,
      capacity: null
    }))
  ];
};

/**
 * Why a selection cannot be submitted on `platform`, or null when it can.
 *
 * - No picked id is in `options`: `NO_AD_ACCOUNT_PICKED`.
 * - Any pick is ineligible: the first reason from `selectionIneligibility`.
 */
export const selectionBlocker = (
  options: readonly AdAccountOption[],
  platform: AdPlatform,
  adAccountIds: readonly string[]
): string | null => {
  if (selectedAdAccountOptions(options, adAccountIds).length === 0) return NO_AD_ACCOUNT_PICKED;
  return selectionIneligibility(options, platform, adAccountIds);
};

/**
 * The first reason a whole selection cannot be delivered on `platform`, or null when every pick is eligible.
 *
 * Each picked id is checked against the other picks.
 * An id missing from `options` is ineligible with `INELIGIBLE_REASONS.unknownAccount`.
 */
export const selectionIneligibility = (
  options: readonly AdAccountOption[],
  platform: AdPlatform,
  adAccountIds: readonly string[]
): IneligibleReason | null => {
  const picked = selectedAdAccountOptions(options, adAccountIds);
  if (picked.length < new Set(adAccountIds).size) return INELIGIBLE_REASONS.unknownAccount;
  for (const option of picked) {
    const others = picked.filter(other => other.adAccountId !== option.adAccountId);
    const eligibility = adAccountEligibility(option, platform, others);
    if (!eligibility.eligible) return eligibility.reason;
  }
  return null;
};

/**
 * Whether one ad account can be picked for a delivery on `platform`, given the ids already picked.
 *
 * Reasons, in precedence order:
 * - The account belongs to the other platform, with a separate reason for Proxima accounts on TikTok.
 * - A brand Meta account has not accepted Meta's custom audience terms.
 * - A Proxima account has no free custom audience slots.
 * - On Meta, an account of the other source (brand or Proxima) is already picked.
 */
export const adAccountEligibility = (
  option: AdAccountOption,
  platform: AdPlatform,
  selected: readonly AdAccountOption[]
): AdAccountEligibility => {
  if (GROUP_PLATFORM[option.group] !== platform) {
    if (option.group === 'metaProxima')
      return { eligible: false, reason: INELIGIBLE_REASONS.proximaOnTiktok };
    return {
      eligible: false,
      reason: platform === 'tiktok' ? INELIGIBLE_REASONS.notTiktok : INELIGIBLE_REASONS.notMeta
    };
  }
  if (option.acceptedTos === false)
    return { eligible: false, reason: INELIGIBLE_REASONS.tosNotAccepted };
  if (option.capacity?.remaining === 0)
    return { eligible: false, reason: INELIGIBLE_REASONS.noFreeSlots };
  if (
    platform === 'meta' &&
    selected.some(picked => GROUP_SOURCE[picked.group] !== GROUP_SOURCE[option.group])
  )
    return { eligible: false, reason: INELIGIBLE_REASONS.mixedGroups };
  return { eligible: true };
};

/** The picked ids as options; ids missing from the lists are dropped. */
export const selectedAdAccountOptions = (
  options: readonly AdAccountOption[],
  adAccountIds: readonly string[]
): AdAccountOption[] => options.filter(option => adAccountIds.includes(option.adAccountId));
